import { BillingInterval, billingPrice } from "./BillingInterval";
import { loadStripe } from "@stripe/stripe-js";
import { useAppContext, useMobile } from "./hooks";
import { useEffect, useState } from "react";
import {
  createCheckoutSessionV2,
  createTrialCheckoutSession,
  getSession,
} from "./api";
import { Tier, Type } from "./types";
import {
  isAllAccessType,
  ALL_ACCESS_PRICE_MAP,
  ALL_ACCESS_PRICE_LABEL_MAP,
} from "./allAccess";
import {
  getStoryStatsConversionValue,
  getStoryStatsPriceLabel,
  getStoryStatsProductInfo,
} from "./storyStatsPricing";
import { pollForSubscriptionUpdate } from "./utils/pollForSubscriptionUpdate";
import {
  Button,
  Center,
  Heading,
  Spinner,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  CheckoutProvider,
  PaymentElement,
  useCheckout,
} from "@stripe/react-stripe-js";
import { datadogRum } from "@datadog/browser-rum";
import { useLocation, useNavigate } from "react-router-dom";

const stripe = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
    "pk_test_51OdfofGBMDZ8Soj5uPtrq4YXgh0viDLGhzQNjGMLIbbbimvoRL4VG1Ftki6A4Nj08mGaZD5naH6A0CxdpIYcScmY00E4i9CoCs",
  {
    betas: ["custom_checkout_beta_5"],
  }
);

export const StripeCheckout = () => {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const tier = query.get("tier");
  const type = query.get("type");
  const isTrial = query.get("trial") === "true";
  const interval: BillingInterval = query.get("billing_interval") === "year" ? "year" : "month";

  return (
    <CheckoutSession
      key={JSON.stringify([type, tier, isTrial, interval])}
      tier={tier}
      type={type}
      isTrial={isTrial}
      interval={interval}
    />
  );
};

// A change in checkout terms must discard the previous session and form together.
const CheckoutSession = ({ tier, type, isTrial, interval }: {
  tier: string | null;
  type: string | null;
  isTrial: boolean;
  interval: BillingInterval;
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { setUser } = useAppContext();

  useEffect(() => {
    let active = true;
    const cleanup = () => { active = false; };
    // Trial flow: type is required (no tier — always GOAT / paid+).
    // Home.tsx may have already stashed a clientSecret via sessionStorage
    // for the new-sub Checkout path; use it if present, otherwise call the
    // backend directly (covers direct-URL access to /checkout?trial=true).
    if (isTrial) {
      if (!type) return;
      const stashedSecret = sessionStorage.getItem("bdl_trial_client_secret");
      const stashedType = sessionStorage.getItem("bdl_trial_type");
      if (stashedSecret && stashedType === type && (sessionStorage.getItem("bdl_trial_interval") || "month") === interval) {
        sessionStorage.removeItem("bdl_trial_client_secret");
        sessionStorage.removeItem("bdl_trial_type");
        setClientSecret(stashedSecret);
        return;
      }
      const startTrial = async () => {
        try {
          const res = await createTrialCheckoutSession(type as Type, interval);
          if (!active) return;
          if ("clientSecret" in res && res.clientSecret) {
            setClientSecret(res.clientSecret);
            return;
          }
          // Paid-upgrade overlay: no checkout UI needed, bounce back to dashboard.
          toast({
            title: "Trial Starting",
            description: "Your 7-day GOAT trial is activating.",
            status: "info",
            isClosable: true,
            duration: 5000,
          });
          navigate("/account", { replace: true });
        } catch (e: any) {
          if (!active) return;
          setError(true);
          const backendMessage = e?.response?.data?.error;
          toast({
            title: "Unable to Start Trial",
            description:
              backendMessage ||
              "Something went wrong. Please try again from the dashboard.",
            status: "warning",
            isClosable: true,
            duration: 8000,
          });
          navigate("/account", { replace: true });
        }
      };
      startTrial();
      return cleanup;
    }

    if (!tier || !type) {
      return;
    }
    const fetchClientSecret = async () => {
      try {
        const res = await createCheckoutSessionV2(tier as Tier, type as Type, interval);
        if (!active) return;
        setClientSecret(res.clientSecret);
      } catch (e: any) {
        if (!active) return;
        setError(true);
        try {
          const freshUser = await getSession();
          if (!active) return;
          setUser(freshUser);
        } catch {}
        if (!active) return;

        const backendMessage = e?.response?.data?.error;
        toast({
          title: "Unable to Start Checkout",
          description:
            backendMessage ||
            "Something went wrong. Please try again from the dashboard.",
          status: "warning",
          isClosable: true,
          duration: 8000,
        });
        navigate("/account", { replace: true });
      }
    };
    fetchClientSecret();
    return cleanup;
  }, [tier, type, isTrial, interval]);

  if (!clientSecret) {
    if (error) return null;
    return (
      <Center w="100%" h="100%">
        <Spinner />
      </Center>
    );
  }

  return (
    <CheckoutProvider
      key={clientSecret}
      stripe={stripe}
      options={{
        clientSecret,
        elementsOptions: {
          appearance: {
            theme: "night",
            labels: "floating",
            variables: {
              colorPrimary: "#60A5FA",
            },
          },
        },
      }}
    >
      <CheckoutForm
        tier={isTrial ? "paid_plus" : tier!}
        type={type!}
        isTrial={isTrial}
      interval={interval}
      />
    </CheckoutProvider>
  );
};

const CheckoutForm = ({
  tier,
  type,
  isTrial,
  interval,
}: {
  tier: string;
  type: string;
  isTrial?: boolean;
  interval: BillingInterval;
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const checkout = useCheckout();
  const { user, setUser } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMobile();

  useEffect(() => {
    checkout.updateEmail(user?.email!);
    // checkout is not memoized properly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const result = await checkout.confirm({ redirect: "if_required" });
      if (result.type === "error") {
        toast({
          title: "Error",
          description: result.error.message,
          status: "error",
          isClosable: true,
        });
      } else {
        datadogRum.addAction("stripe_callback", {
          type,
          tier,
          cookie: document.cookie,
          email: user?.email,
        });
        const dollars =
          ALL_ACCESS_PRICE_MAP[type]
            ? ALL_ACCESS_PRICE_MAP[type]
            : getStoryStatsConversionValue(type) ??
              (tier === "paid" ? 9.99 : 39.99);
        (window as any).gtag("event", "conversion", {
          send_to: "AW-16760575486/17bXCKTZhOQZEP6riLg-",
          value: dollars,
          currency: "USD",
          transaction_id: user?.email,
        });
        const expectedTier = (tier === "paid_plus" ? "paid+" : tier) as Tier;
        try {
          const { user: updatedUser, confirmed } =
            await pollForSubscriptionUpdate(expectedTier, type as Type);
          setUser(updatedUser);

          if (confirmed) {
            toast({
              title: "Purchase Complete",
              description: "Your subscription has been activated.",
              status: "success",
              isClosable: true,
              duration: 5000,
            });
          } else {
            toast({
              title: "Purchase Complete",
              description:
                "Your subscription is being activated. It may take a few minutes to appear.",
              status: "info",
              isClosable: true,
              duration: null,
            });
          }
        } catch (e) {
          console.warn("Failed to poll subscription update after checkout", e);
          toast({
            title: "Purchase Complete",
            description:
              "Your subscription is being activated. It may take a few minutes to appear.",
            status: "info",
            isClosable: true,
            duration: null,
          });
        }
        navigate("/account", { replace: true });
      }
    } catch (e) {
      toast({
        title: "Error",
        description: "Please contact support at hello@balldontlie.io for help",
        status: "error",
        isClosable: true,
        duration: null,
      });
    }
    setIsLoading(false);
  };

  const w = isMobile ? "100%" : "75%";

  const priceLabel = interval === "year" ? billingPrice(type, tier, interval) :
    ALL_ACCESS_PRICE_LABEL_MAP[type] ||
    getStoryStatsPriceLabel(type) ||
    (tier === "paid" ? "$9.99/mo" : "$39.99/mo");
  const storyStatsInfo = getStoryStatsProductInfo(type);
  const tierName = isAllAccessType(type)
    ? "ALL-ACCESS"
    : storyStatsInfo
      ? storyStatsInfo.displayName
    : tier === "paid"
      ? "ALL-STAR"
      : "GOAT";

  return (
    <VStack w="100%" h="100%" align="center" justify="center" gap={4}>
      <VStack w={w} align="flex-start">
        {isTrial ? (
          <>
            <Heading mb={0}>
              Start your 7-day {tierName} trial
              {isAllAccessType(type) || storyStatsInfo ? "" : ` · ${type.toUpperCase()}`}
            </Heading>
            <Heading size="md" fontWeight="normal" color="gray.500">
              You won't be charged until the trial ends. Cancel anytime.
            </Heading>
          </>
        ) : (
          <>
            <Heading mb={0}>
              {isAllAccessType(type)
                ? "ALL-ACCESS"
                : storyStatsInfo
                  ? storyStatsInfo.displayName
                  : type.toUpperCase()}
            </Heading>
            {ALL_ACCESS_PRICE_LABEL_MAP[type] || storyStatsInfo ? (
              <Heading size="md">{priceLabel}</Heading>
            ) : (
              <Heading size="md">
                {tier === "paid" ? "ALL-STAR" : "GOAT"}{" "}
                {priceLabel}
              </Heading>
            )}
          </>
        )}
      </VStack>
      <form style={{ width: w }}>
        <PaymentElement options={{ layout: "accordion" }} />
      </form>

      <Button
        w={w}
        colorScheme="brand"
        disabled={isLoading}
        isLoading={isLoading}
        onClick={handleConfirm}
      >
        {isTrial ? "Start Free Trial" : "Confirm Payment"}
      </Button>

      {isTrial && (
        <Heading
          size="xs"
          fontWeight="normal"
          color="gray.500"
          textAlign="center"
          w={w}
          mt={-2}
        >
          At the end of the 7-day trial you'll be automatically charged{" "}
          {priceLabel}. Cancel anytime in your dashboard.
        </Heading>
      )}
    </VStack>
  );
};
